/*
 * The USCIS pane is the last nav row under the case explorer header, above
 * Case Files. Collapsed, its row answers "where does this case stand with
 * USCIS?" at a glance (tone dot + short status). Expanded, it
 * reads like a status page: one accordion row per receipt with a timeline,
 * hover-revealed copy / remove, and a paste-friendly token input for adding
 * many receipt numbers at once.
 *
 * shadcn/ui only: no Chakra, no Subframe kit.
 */
import { Check, ChevronDown, Copy, RefreshCw, Trash2, X } from "lucide-react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import {
  type ClipboardEvent,
  type FC,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "sonner";

import {
  Accordion,
  AccordionContent,
  AccordionItem
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { FieldError } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from "@/components/ui/input-group";
import { Kbd } from "@/components/ui/kbd";
import { paneTitleClass } from "@/components/ui/pane-header";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { USCISReceipt } from "../../../api/types/case";
import { useCase } from "../../../contexts/CaseContext";
import { useOnCaseInvalidate } from "../context/caseInvalidation";
import {
  aggregateTone,
  chipLabel,
  addedToast,
  formatReceiptDate,
  formatShortDate,
  friendlyUscisError,
  suggestedReceipts,
  isValidReceiptNumber,
  latestUpdateAcross,
  normalizeReceiptNumber,
  parseReceiptTokens,
  receiptSummary,
  relativeUpdated,
  type ReceiptSummary,
  type StatusTone
} from "../header/uscisModel";
import { useUscisPaneOpen } from "../layout/ideLayoutStore";
import { useCaseRoute } from "../useCaseRoute";
import { useKeyFacts } from "../../keyFacts/hooks/useKeyFacts";

/* ---------------------------------------------------------------------- */
/* Tone styling                                                            */
/* ---------------------------------------------------------------------- */

const TONE_DOT: Record<StatusTone, string> = {
  approved: "bg-success",
  attention: "bg-warn",
  denied: "bg-danger",
  error: "bg-danger",
  pending: "bg-primary",
  none: "bg-fg-4"
};

const TONE_RAIL: Record<StatusTone, string> = {
  approved: "data-[state=open]:border-l-success",
  attention: "data-[state=open]:border-l-warn",
  denied: "data-[state=open]:border-l-danger",
  error: "data-[state=open]:border-l-danger",
  pending: "data-[state=open]:border-l-primary",
  none: "data-[state=open]:border-l-border"
};

/** Only the urgent tones tint the header's status pill; the rest stay plain. */
const PILL_TINT: Record<StatusTone, string> = {
  approved: "text-fg-2",
  pending: "text-fg-2",
  none: "text-fg-4",
  attention: "bg-warn-soft px-2 text-warn",
  denied: "bg-danger-soft px-2 text-danger",
  error: "bg-danger-soft px-2 text-danger"
};

const URGENT: ReadonlySet<StatusTone> = new Set([
  "attention",
  "denied",
  "error"
]);

const ToneDot: FC<{
  tone: StatusTone;
  className?: string;
  pulse?: boolean;
}> = ({ tone, className, pulse = false }) => (
  <span
    aria-hidden
    data-tone={tone}
    className={cn(
      "inline-block size-1.5 shrink-0 rounded-full",
      TONE_DOT[tone],
      pulse && "animate-pulse",
      className
    )}
  />
);

const MONO = "font-mono tracking-wide";
const RECEIPT_HELP = "3 letters + 10 digits, e.g. IOE0123456789";

/* ---------------------------------------------------------------------- */
/* Receipt row                                                             */
/* ---------------------------------------------------------------------- */

const CopyButton: FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      className="text-muted-foreground"
      aria-label="Copy receipt number"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </Button>
  );
};

/** Two-step inline remove: trash -> "Remove" (auto-reverts after 3s). */
const RemoveButton: FC<{
  onConfirm: () => void;
  deleting: boolean;
  onConfirmingChange: (confirming: boolean) => void;
}> = ({ onConfirm, deleting, onConfirmingChange }) => {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<number | null>(null);

  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const set = (next: boolean) => {
    clear();
    setConfirming(next);
    onConfirmingChange(next);
    if (next) {
      timer.current = window.setTimeout(() => {
        setConfirming(false);
        onConfirmingChange(false);
      }, 3000);
    }
  };

  useEffect(() => clear, []);

  if (deleting) {
    return (
      <span className="inline-flex size-6 items-center justify-center text-muted-foreground">
        <Spinner className="size-3.5" />
      </span>
    );
  }

  if (confirming) {
    return (
      <Button
        type="button"
        variant="destructive"
        size="xs"
        autoFocus
        className="h-6"
        onBlur={() => set(false)}
        onClick={() => {
          clear();
          onConfirm();
        }}
      >
        Remove
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      className="text-muted-foreground hover:text-destructive"
      aria-label="Remove receipt"
      onClick={() => set(true)}
    >
      <Trash2 className="size-3" />
    </Button>
  );
};

const Timeline: FC<{ summary: ReceiptSummary }> = ({ summary }) => {
  const entries = [
    ...summary.history.map((h) => ({
      key: `${h.date}-${h.status_en}`,
      date: formatShortDate(h.date),
      text: h.status_en,
      terminal: false
    })),
    ...(summary.submittedDate
      ? [
          {
            key: "filed",
            date: formatShortDate(summary.submittedDate),
            text: `Filed ${formatReceiptDate(summary.submittedDate)}`,
            terminal: true
          }
        ]
      : [])
  ];

  if (!entries.length) {
    return (
      <p className="text-xs text-muted-foreground">
        No status updates yet. USCIS usually posts the first update within a few
        days of receipt.
      </p>
    );
  }

  return (
    <ol className="flex flex-col pt-1.5 pb-1">
      {entries.map((entry, index) => {
        const last = index === entries.length - 1;
        return (
          <li
            key={entry.key}
            className="grid grid-cols-[14px_44px_1fr] items-start gap-x-1.5"
          >
            <span className="relative flex h-full min-h-6 justify-center">
              {!last ? (
                <span className="absolute top-2.5 -bottom-1 w-px bg-border" />
              ) : null}
              <span
                className={cn(
                  "relative mt-1.25 size-1.5 rounded-full",
                  entry.terminal
                    ? "border border-muted-foreground/60 bg-transparent"
                    : index === 0
                    ? TONE_DOT[summary.tone]
                    : "bg-muted-foreground/40"
                )}
              />
            </span>
            <span className="pt-px text-xs tabular-nums text-muted-foreground">
              {entry.date}
            </span>
            <span
              className={cn(
                "pb-2 text-xs leading-4",
                entry.terminal
                  ? "text-muted-foreground"
                  : index === 0
                  ? "text-foreground"
                  : "text-fg-2"
              )}
            >
              {entry.text}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const ReceiptItem: FC<{
  receipt: USCISReceipt;
  onDelete: (receiptId: string) => void;
  onRetry: () => Promise<unknown> | void;
  deleting: boolean;
}> = ({ receipt, onDelete, onRetry, deleting }) => {
  const summary = useMemo(() => receiptSummary(receipt), [receipt]);
  const friendly = summary.error ? friendlyUscisError(summary.error) : null;
  const [confirming, setConfirming] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const updated = relativeUpdated(summary.latestDate);
  const headline = summary.error
    ? "Status unavailable right now"
    : summary.latestStatus ?? "Awaiting first update";

  return (
    <AccordionItem
      value={summary.receiptId}
      data-testid={`receipt-${summary.number}`}
      className={cn(
        "group/item relative border-l border-l-transparent transition-colors",
        TONE_RAIL[summary.tone]
      )}
    >
      <AccordionPrimitive.Header className="relative flex">
        <AccordionPrimitive.Trigger
          className={cn(
            "group/trigger flex flex-1 items-center gap-2.5 py-2.5 pr-24 pl-3 text-left text-xs outline-none transition-colors",
            "hover:bg-fill-1 focus-visible:bg-fill-1"
          )}
        >
          <ToneDot tone={summary.tone} className="size-2" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span
              className={cn(
                "truncate font-medium",
                summary.error ? "text-muted-foreground" : "text-foreground"
              )}
            >
              {headline}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={MONO}>{summary.number}</span>
              {summary.formType ? (
                <Badge
                  variant="secondary"
                  className="h-4 px-1.5 text-xs font-medium"
                >
                  {summary.formType}
                </Badge>
              ) : null}
              {updated ? <span>· Updated {updated}</span> : null}
            </span>
          </span>
          <span
            aria-hidden
            className="absolute top-1/2 right-3 inline-flex size-6 -translate-y-1/2 items-center justify-center text-fg-4"
          >
            <ChevronDown className="size-3 transition-transform duration-150 group-data-[state=open]/trigger:rotate-180" />
          </span>
        </AccordionPrimitive.Trigger>

        <div
          data-confirming={confirming}
          className={cn(
            "absolute top-1/2 right-10 flex -translate-y-1/2 items-center gap-0.5 transition-opacity duration-150",
            "opacity-0 group-hover/item:opacity-100 focus-within:opacity-100 data-[confirming=true]:opacity-100"
          )}
        >
          {!confirming ? <CopyButton value={summary.number} /> : null}
          <RemoveButton
            deleting={deleting}
            onConfirmingChange={setConfirming}
            onConfirm={() => onDelete(summary.receiptId)}
          />
        </div>
      </AccordionPrimitive.Header>

      <AccordionContent className="pr-3 pl-7.5 pb-1">
        {friendly ? (
          <Alert className="mt-1 mb-2 py-1.5" data-testid="uscis-error">
            <AlertTitle className="text-xs">{friendly.title}</AlertTitle>
            <AlertDescription className="text-xs">
              {friendly.body}
            </AlertDescription>
            {friendly.retryable ? (
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="mt-1.5 w-fit gap-1"
                disabled={retrying}
                onClick={() => {
                  setRetrying(true);
                  void Promise.resolve(onRetry()).finally(() =>
                    setRetrying(false)
                  );
                }}
              >
                <RefreshCw
                  className={cn("size-3", retrying && "animate-spin")}
                />
                Try again
              </Button>
            ) : null}
          </Alert>
        ) : (
          <Timeline summary={summary} />
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

/* ---------------------------------------------------------------------- */
/* Token input                                                             */
/* ---------------------------------------------------------------------- */

type TokenState = "valid" | "invalid" | "duplicate";

const TOKEN_CLASS: Record<TokenState, string> = {
  valid: "border-border text-foreground",
  invalid: "border-destructive/60 bg-destructive/10 text-destructive",
  duplicate: "border-border text-muted-foreground"
};

const TOKEN_TITLE: Record<TokenState, string | undefined> = {
  valid: undefined,
  invalid: "Must be 3 letters + 10 digits",
  duplicate: "Already tracked"
};

const AddReceipts: FC<{
  existingNumbers: ReadonlySet<string>;
  onSubmit: (numbers: string[]) => Promise<void>;
  submitting: boolean;
  autoFocus?: boolean;
}> = ({ existingNumbers, onSubmit, submitting, autoFocus = false }) => {
  const [tokens, setTokens] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const stateOf = (token: string): TokenState =>
    existingNumbers.has(token)
      ? "duplicate"
      : isValidReceiptNumber(token)
      ? "valid"
      : "invalid";

  const merge = (current: string[], incoming: string[]) => {
    const next = [...current];
    for (const token of incoming) if (!next.includes(token)) next.push(token);
    return next;
  };

  const commit = (raw: string): string[] => {
    const parsed = parseReceiptTokens(raw);
    const next = merge(tokens, parsed);
    setTokens(next);
    setText("");
    setError(null);
    return next;
  };

  const removeToken = (token: string) => {
    setTokens((prev) => prev.filter((t) => t !== token));
    setError(null);
    inputRef.current?.focus();
  };

  const submit = async () => {
    const all = text.trim() ? commit(text) : tokens;
    if (!all.length) {
      setError("Enter at least one receipt number.");
      return;
    }
    const invalidCount = all.filter((t) => stateOf(t) === "invalid").length;
    if (invalidCount) {
      setError(
        invalidCount === 1
          ? "1 number isn't 3 letters + 10 digits"
          : `${invalidCount} numbers aren't 3 letters + 10 digits`
      );
      return;
    }
    const valid = all.filter((t) => stateOf(t) === "valid");
    if (!valid.length) {
      setError("Those receipt numbers are already being tracked.");
      return;
    }
    await onSubmit(valid);
    setTokens([]);
    setText("");
    setError(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey || !text.trim()) {
        void submit();
      } else {
        commit(text);
      }
      return;
    }
    if ((e.key === "," || e.key === " " || e.key === "Tab") && text.trim()) {
      e.preventDefault();
      commit(text);
      return;
    }
    if (e.key === "Backspace" && !text && tokens.length) {
      e.preventDefault();
      setTokens((prev) => prev.slice(0, -1));
      setError(null);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData?.getData("text") ?? "";
    if (!pasted.trim()) return;
    e.preventDefault();
    commit(`${text} ${pasted}`);
  };

  const validCount = tokens.filter((t) => stateOf(t) === "valid").length;
  const pendingValid = text.trim() && isValidReceiptNumber(text) ? 1 : 0;
  const trackCount = validCount + pendingValid;
  const invalidCount = tokens.filter((t) => stateOf(t) === "invalid").length;
  const hasInvalid = invalidCount > 0;
  const shownError =
    error ??
    (invalidCount === 0
      ? null
      : invalidCount === 1
      ? "1 number isn't 3 letters + 10 digits"
      : `${invalidCount} numbers aren't 3 letters + 10 digits`);

  return (
    <div className="flex flex-col gap-2">
      <InputGroup
        className={cn(
          "h-auto min-h-8 flex-wrap gap-1 bg-background px-1.5 py-1",
          shownError && "border-destructive ring-3 ring-destructive/20"
        )}
      >
        {tokens.map((token) => {
          const state = stateOf(token);
          return (
            <Badge
              key={token}
              variant="outline"
              title={TOKEN_TITLE[state]}
              data-state={state}
              className={cn(
                "h-5 gap-1 rounded-md pr-0.5 pl-1.5 text-xs font-normal",
                MONO,
                TOKEN_CLASS[state]
              )}
            >
              {token}
              {state === "duplicate" ? (
                <span className="font-sans text-xs tracking-normal text-fg-3">
                  tracked
                </span>
              ) : null}
              <button
                type="button"
                aria-label={`Remove ${token}`}
                className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => removeToken(token)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          );
        })}
        <InputGroupInput
          ref={inputRef}
          value={text}
          autoFocus={autoFocus}
          aria-label="Receipt numbers"
          aria-invalid={Boolean(shownError) || undefined}
          placeholder={
            tokens.length ? "Add another…" : "Paste or type receipt numbers"
          }
          className={cn("h-6 min-w-32 flex-1 px-1 text-xs", MONO)}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (text.trim()) commit(text);
          }}
        />
        {text.trim() ? (
          <InputGroupAddon align="inline-end" className="py-0 pr-1">
            <Kbd className="h-4 min-w-4 text-xs">↵</Kbd>
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      <div className="flex items-center justify-between gap-3">
        {shownError ? (
          <FieldError className="text-xs">{shownError}</FieldError>
        ) : (
          <span className="text-xs text-muted-foreground">{RECEIPT_HELP}</span>
        )}
        <Button
          variant="primary"
          type="button"
          size="sm"
          className="shrink-0 gap-1 text-xs"
          disabled={submitting || hasInvalid || trackCount === 0}
          onClick={() => void submit()}
        >
          {submitting ? <Spinner className="size-3.5" /> : null}
          {trackCount <= 1 ? "Track receipt" : `Track ${trackCount} receipts`}
        </Button>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Body                                                                    */
/* ---------------------------------------------------------------------- */

/** Open the most urgent receipt, or the only one, so the reason shows first. */
const defaultOpenItems = (receipts: readonly USCISReceipt[]): string[] => {
  const urgent = receipts.find((r) => URGENT.has(receiptSummary(r).tone));
  const first = urgent ?? (receipts.length === 1 ? receipts[0] : undefined);
  return first ? [first.receipt_id] : [];
};

const UscisBody: FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const {
    uscisReceipts,
    isLoadingReceipts,
    receiptsError,
    addUSCISReceipt,
    deleteUSCISReceipt
  } = useCase();
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<string[]>(() =>
    defaultOpenItems(uscisReceipts)
  );

  const existingNumbers = useMemo(
    () =>
      new Set(
        uscisReceipts.map((r) => normalizeReceiptNumber(r.uscis_receipt_number))
      ),
    [uscisReceipts]
  );

  const { individualId } = useCaseRoute();
  const keyFacts = useKeyFacts(individualId);
  const suggestions = useMemo(
    () => suggestedReceipts(keyFacts.data?.facts ?? [], existingNumbers),
    [keyFacts.data?.facts, existingNumbers]
  );

  const handleAdd = async (numbers: string[]) => {
    setSubmitting(true);
    try {
      const added = await Promise.all(numbers.map((n) => addUSCISReceipt(n)));
      const { title, description, tone } = addedToast(added);
      toast[tone](title, { description });
    } catch (err) {
      toast.error("Couldn't add receipt", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (receiptId: string) => {
    setDeletingId(receiptId);
    try {
      await deleteUSCISReceipt(receiptId);
      toast.success("Receipt removed");
    } catch (err) {
      toast.error("Couldn't remove receipt", {
        description: err instanceof Error ? err.message : undefined
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1">
        {isLoadingReceipts ? (
          <div className="flex flex-col divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-3">
                <Skeleton className="size-2 rounded-full" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-2.5 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : receiptsError ? (
          <div className="px-2 py-2">
            <Alert variant="destructive">
              <AlertTitle className="text-xs">
                Couldn't load receipts
              </AlertTitle>
              <AlertDescription className="text-xs">
                {receiptsError.message}
              </AlertDescription>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="mt-1 w-fit"
                onClick={onRetry}
              >
                Retry
              </Button>
            </Alert>
          </div>
        ) : !uscisReceipts.length ? (
          <Empty className="gap-2 border-none p-3">
            <EmptyHeader className="gap-1.5">
              <EmptyMedia variant="icon" className="mb-0 size-7">
                <ToneDot tone="none" className="size-2" />
              </EmptyMedia>
              <EmptyTitle className="text-base">
                Track this case with USCIS
              </EmptyTitle>
              <EmptyDescription className="text-xs leading-4">
                Add one or more receipt numbers and every status update will
                show here and in this pane's header.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ScrollArea className="h-full">
            <Accordion
              type="multiple"
              value={openItems}
              onValueChange={setOpenItems}
            >
              {uscisReceipts.map((receipt) => (
                <ReceiptItem
                  key={receipt.receipt_id}
                  receipt={receipt}
                  onDelete={handleDelete}
                  onRetry={onRetry}
                  deleting={deletingId === receipt.receipt_id}
                />
              ))}
            </Accordion>
          </ScrollArea>
        )}
      </div>

      <div className="shrink-0 border-t border-border bg-fill-1 p-2">
        {suggestions.length && !isLoadingReceipts ? (
          <div
            className="mb-2 flex flex-col gap-1"
            data-testid="uscis-suggestions"
          >
            <span className="text-xs text-muted-foreground">
              Found in the case's documents
            </span>
            {suggestions.map((s) => (
              <div
                key={s.number}
                className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className={cn(MONO, "text-xs")}>{s.number}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {s.from}
                  </span>
                </span>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => void handleAdd([s.number])}
                >
                  Track
                </Button>
              </div>
            ))}
          </div>
        ) : null}
        <AddReceipts
          existingNumbers={existingNumbers}
          onSubmit={handleAdd}
          submitting={submitting}
        />
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Pane                                                                    */
/* ---------------------------------------------------------------------- */

const paneTooltip = (receipts: readonly USCISReceipt[]): string => {
  if (!receipts.length) return "Track USCIS receipts";
  if (receipts.length > 1) {
    const updated = relativeUpdated(latestUpdateAcross(receipts));
    return `${receipts.length} USCIS receipts tracked${
      updated ? ` · updated ${updated}` : ""
    }`;
  }
  const summary = receiptSummary(receipts[0]);
  if (summary.error) return "USCIS status unavailable";
  const updated = relativeUpdated(summary.latestDate);
  return [
    summary.latestStatus ?? "Awaiting first update",
    updated && `Updated ${updated}`
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * The USCIS pane of the case files column. Its open state is remembered per
 * case; collapsed, the header alone carries the case's USCIS standing.
 */
export const UscisPane: FC = () => {
  const { uscisReceipts, isLoadingReceipts, refetchReceipts } = useCase();
  const { individualId } = useCaseRoute();
  const [open, setOpen] = useUscisPaneOpen(individualId);
  const [refreshing, setRefreshing] = useState(false);

  // The agent's track/untrack tools report `invalidate: ["receipts"]`.
  useOnCaseInvalidate(individualId, ["receipts"], () => {
    void refetchReceipts();
  });

  const tone = aggregateTone(uscisReceipts);
  const label = uscisReceipts.length
    ? chipLabel(uscisReceipts)
    : isLoadingReceipts
    ? "Checking…"
    : "Not Tracked";

  const refresh = async () => {
    setRefreshing(true);
    try {
      await refetchReceipts();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="USCIS Status"
      data-testid="uscis-pane"
      data-state={open ? "open" : "closed"}
      className={cn(
        "flex min-h-0 shrink-0 flex-col border-b border-line-2",
        open && "basis-[46%]"
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center gap-0.5 px-1 pb-1.5",
          open && "border-b border-line-2"
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              data-active={open || undefined}
              className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-base text-fg-2 outline-none transition-colors duration-100 hover:bg-fill-1 hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 data-active:bg-fill-1 data-active:text-fg"
            >
              <span className={cn("min-w-0 flex-1 truncate", paneTitleClass)}>
                USCIS Status
              </span>
              <span
                data-testid="uscis-status"
                className={cn(
                  "inline-flex h-5 min-w-0 shrink-0 items-center gap-1.5 rounded-full text-sm",
                  PILL_TINT[tone]
                )}
              >
                <ToneDot tone={tone} pulse={refreshing} />
                <span className="truncate">{label}</span>
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {open ? "Collapse" : paneTooltip(uscisReceipts)}
          </TooltipContent>
        </Tooltip>
        {open ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground"
            aria-label="Refresh USCIS status"
            disabled={refreshing}
            onClick={() => void refresh()}
          >
            <RefreshCw
              className={cn("size-3.5", refreshing && "animate-spin")}
            />
          </Button>
        ) : null}
      </div>
      {open ? <UscisBody onRetry={() => void refresh()} /> : null}
    </div>
  );
};
